import {BillingPeriod} from '../types/billing';
import {logger} from '../utils/logger';
import {verifyCheckoutSession} from '../utils/stripe';

// Interface for Stripe session validation response
export interface StripeSessionValidation {
  valid: boolean;
  error?: string;
  subscription?: {
    plan: 'free' | 'pro';
    billing?: BillingPeriod;
    customerId?: string;
    subscriptionId?: string;
    currentPeriodEnd?: Date;
  };
}

/**
 * Validate a Stripe checkout session
 * @param sessionId The Stripe checkout session ID
 * @returns A promise resolving to session validation details
 */
export async function validateStripeSession(sessionId: string): Promise<StripeSessionValidation> {
  try {
    logger.info('Validating Stripe session:', sessionId);

    // Use our utility to verify the session with Stripe
    const result = await verifyCheckoutSession(sessionId);

    if (result.status === 'error' || !result.data) {
      console.error('Session validation failed:', result.error);
      return {
        valid: false,
        error: result.error || 'Failed to validate checkout session',
      };
    }

    // Extract the subscription data from the verification result
    const {plan, billing, customerId, subscriptionId, currentPeriodEnd} = result.data;

    logger.info('Session validated successfully:', {plan, billing});

    return {
      valid: true,
      subscription: {
        plan,
        billing,
        customerId,
        subscriptionId,
        currentPeriodEnd: currentPeriodEnd ? new Date(currentPeriodEnd * 1000) : undefined,
      },
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error validating session';
    console.error('Error validating Stripe session:', errorMessage);

    return {
      valid: false,
      error: errorMessage,
    };
  }
}

/**
 * Get customer email from Stripe customer ID
 * @param customerId The Stripe customer ID
 * @returns A promise resolving to the customer email or null if not found
 */
export async function getStripeCustomerEmail(customerId: string): Promise<string | null> {
  try {
    logger.info('Retrieving customer email for:', customerId);

    // Try the Netlify serverless function first
    try {
      const netlifyResponse = await fetch(`/.netlify/functions/get-customer?id=${encodeURIComponent(customerId)}`);

      if (netlifyResponse.ok) {
        const customerData = await netlifyResponse.json();
        if (customerData && customerData.email) {
          logger.info('Successfully retrieved customer email from Netlify function');
          return customerData.email;
        }
      }
    } catch (netlifyError) {
      console.warn('Failed to get customer from Netlify function, trying fallback:', netlifyError);
    }

    // Fallback to marketing site API if Netlify function fails
    const marketingUrl = import.meta.env.VITE_APP_DOMAIN || 'http://localhost:3000';
    const response = await fetch(`${marketingUrl}/api/stripe/customer?id=${encodeURIComponent(customerId)}`);

    if (!response.ok) {
      throw new Error(`Failed to retrieve customer: ${response.statusText}`);
    }

    const data = await response.json();

    if (data && data.email) {
      return data.email;
    }

    return null;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error getting customer';
    console.error('Error retrieving customer email:', errorMessage);
    return null;
  }
}

/**
 * Create a manual registration handler for cases where Stripe redirect fails
 * This can be used to manually complete registration with subscription details
 */
export async function getStripeCheckoutDetails(sessionId: string): Promise<{
  email?: string;
  plan: 'free' | 'pro';
  billing?: BillingPeriod;
} | null> {
  try {
    const validation = await validateStripeSession(sessionId);

    if (!validation.valid || !validation.subscription) {
      return null;
    }

    return {
      plan: validation.subscription.plan,
      billing: validation.subscription.billing,
      // Email would require additional API call with Stripe customer ID
      // which we're omitting for simplicity
    };
  } catch (error) {
    logger.error('Error getting checkout details:', error);
    return null;
  }
}

/**
 * Create a Stripe checkout session for pro plan subscription
 * @param billingPeriod The billing period (yearly or quarterly)
 * @param customerId Optional Stripe customer ID for existing customers
 * @param userId Optional Firebase user ID for new customers
 * @param email Optional user email for new customers
 * @returns A promise resolving to the checkout session URL
 */
export async function createCheckoutSession(
  billingPeriod: BillingPeriod,
  customerId?: string,
  userId?: string,
  email?: string
): Promise<string> {
  try {
    logger.info('Creating Stripe checkout session for:', billingPeriod);

    // Get the price ID from environment variables
    const priceId =
      billingPeriod === 'yearly'
        ? import.meta.env.VITE_STRIPE_YEARLY_PRICE_ID
        : billingPeriod === 'monthly'
        ? import.meta.env.VITE_STRIPE_MONTHLY_PRICE_ID
        : import.meta.env.VITE_STRIPE_QUARTERLY_PRICE_ID;

    if (!priceId) {
      throw new Error('Stripe price ID not configured');
    }

    // Call our Netlify function to create the checkout session
    const response = await fetch('/.netlify/functions/create-checkout-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        priceId,
        customerId,
        userId,
        email,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create checkout session');
    }

    const {url} = await response.json();

    if (!url) {
      throw new Error('No checkout URL returned from server');
    }

    // Redirect to the checkout URL
    window.location.href = url;
    return url;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error creating checkout session';
    console.error('Error creating checkout session:', errorMessage);
    throw error;
  }
}

/**
 * Get subscription data for a customer
 * @param customerId The Stripe customer ID
 * @returns A promise resolving to the customer's subscriptions
 */
export async function getSubscriptionStatus(customerId: string) {
  try {
    logger.info('Getting subscription status for customer:', customerId);

    const response = await fetch(
      `/.netlify/functions/get-subscription-status?customerId=${encodeURIComponent(customerId)}`
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to get subscription status');
    }

    const data = await response.json();
    return data.subscriptions;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error getting subscription status';
    console.error('Error getting subscription status:', errorMessage);
    throw error;
  }
}

/**
 * Cancel a subscription
 * @param subscriptionId The Stripe subscription ID
 * @returns A promise resolving to the cancelled subscription
 */
export async function cancelSubscription(subscriptionId: string) {
  try {
    logger.info('Cancelling subscription:', subscriptionId);

    const response = await fetch('/.netlify/functions/cancel-subscription', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        subscriptionId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to cancel subscription');
    }

    const data = await response.json();
    return data.subscription;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error cancelling subscription';
    console.error('Error cancelling subscription:', errorMessage);
    throw error;
  }
}
